import {
  ArrowBack,
  ArrowForward,
  FlipToBack,
  Shuffle,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { FinsightEntityFieldValue } from 'components/renderField';
import React, { useMemo, useState } from 'react';
import { FinsightEntity, FinsightEntityField } from 'shared';
import { formatName, sortFields } from 'utils/fields';

export default function FlashcardsView({
  entities,
}: {
  entities?: FinsightEntity[];
}) {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [shuffled, setShuffled] = useState(false);

  // Shuffle function
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Memoized entities (shuffled or original order)
  const displayEntities = useMemo(() => {
    if (!entities || entities.length === 0) return [];
    return shuffled ? shuffleArray(entities) : entities;
  }, [entities, shuffled]);

  // Reset index when entities change or shuffle
  React.useEffect(() => {
    setCurrentIndex(0);
    setIsFlipped(false);
  }, [displayEntities]);

  const currentEntity = displayEntities[currentIndex];

  const handleNext = () => {
    if (currentIndex < displayEntities.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleShuffle = () => {
    setShuffled(!shuffled);
  };

  if (!entities || entities.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          No entities available for flashcards
        </Typography>
      </Stack>
    );
  }

  // Get the fields to display on the back of the card
  const backFields = currentEntity
    ? sortFields(Object.values(currentEntity.extraFieldsMeta)).filter(
        (field: FinsightEntityField) =>
          field.name !== 'name' && field.name !== 'description',
      )
    : [];

  return (
    <Stack gap={3} alignItems="center">
      {/* Controls */}
      <Stack direction="row" gap={2} alignItems="center">
        <Button
          variant={shuffled ? 'contained' : 'outlined'}
          startIcon={<Shuffle />}
          onClick={handleShuffle}
          size="small"
        >
          {shuffled ? 'Shuffled' : 'Shuffle'}
        </Button>
        <Chip
          label={`${currentIndex + 1} of ${displayEntities.length}`}
          variant="outlined"
        />
      </Stack>

      {/* Flashcard */}
      <Box
        sx={{
          perspective: '1000px',
          width: '100%',
          maxWidth: 600,
          height: 400,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.6s',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* Front of card */}
          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              border: `2px solid ${theme.palette.primary.main}`,
            }}
            onClick={handleFlip}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
              }}
            >
              <Chip
                label={formatName(currentEntity?.types[0] || '')}
                color="primary"
                size="small"
              />
              <Typography variant="h4" component="h2" fontWeight="bold">
                {currentEntity?.name || 'Untitled'}
              </Typography>
              {currentEntity?.description && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: '80%' }}
                >
                  {currentEntity.description}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" gap={1} mt={2}>
                <FlipToBack fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  Click to flip
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Back of card */}
          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              border: `2px solid ${theme.palette.secondary.main}`,
            }}
            onClick={handleFlip}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflow: 'auto',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" fontWeight="bold">
                  Details
                </Typography>
                <Chip
                  label={formatName(currentEntity?.types[0] || '')}
                  color="secondary"
                  size="small"
                />
              </Stack>

              {backFields.length > 0 ? (
                <Stack gap={2}>
                  {backFields.map((field: FinsightEntityField) => (
                    <Box key={field.name}>
                      <FinsightEntityFieldValue
                        viewType="entity_sidebar"
                        editable={false}
                        field={field}
                        value={currentEntity?.extraFields?.[field.name]}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Stack alignItems="center" justifyContent="center" flexGrow={1}>
                  <Typography variant="body2" color="text.secondary">
                    No additional fields to display
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" alignItems="center" gap={1} mt="auto">
                <FlipToBack fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  Click to flip back
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Navigation */}
      <Stack direction="row" gap={2} alignItems="center">
        <IconButton
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          size="large"
        >
          <ArrowBack />
        </IconButton>

        <Button
          variant="outlined"
          onClick={handleFlip}
          startIcon={<FlipToBack />}
        >
          {isFlipped ? 'Show Front' : 'Show Back'}
        </Button>

        <IconButton
          onClick={handleNext}
          disabled={currentIndex === displayEntities.length - 1}
          size="large"
        >
          <ArrowForward />
        </IconButton>
      </Stack>
    </Stack>
  );
}
